import * as SelectPrimitive from "@radix-ui/react-select";
import * as React from "react";

import { FieldTrigger, FieldTriggerButton } from "../field-trigger";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
} from "../select";

type SelectRootProps = React.ComponentProps<typeof SelectPrimitive.Root>;

interface SelectFilterOption {
  /** The text to display for the option. */
  label: string;
  /** The unique value associated with the option. */
  value: string;
  /** Optional icon component to display alongside the option. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Whether the option is disabled. */
  disabled?: boolean;
}

interface SelectFilterProps extends Pick<
  SelectRootProps,
  "value" | "disabled"
> {
  className?: string;
  id?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false" | "grammar" | "spelling";
  /** An array of option objects to be displayed in the select. */
  options: SelectFilterOption[];
  /** Callback function triggered when the selected value changes. Receives undefined when cleared. */
  onValueChange: (value: string | undefined) => void;
  /** Placeholder text to be displayed when no value is selected. */
  placeholder?: string;
  /** Accessible name for the clear button on the trigger. */
  clearLabel: string;
}

const SelectFilter: React.FC<SelectFilterProps> = ({
  options,
  value,
  onValueChange,
  placeholder = "Select option",
  clearLabel,
  disabled = false,
  className,
  id,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
}) => {
  // Radix Select doesn't support clearing a controlled value back to placeholder.
  // Changing the key forces a remount, creating a fresh instance with no selection.
  const [resetKey, setResetKey] = React.useState(0);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const handleClear = () => {
    setResetKey((k) => k + 1);
    onValueChange(undefined);
    // Defer focus until after React re-renders with the new key (trigger remounts).
    setTimeout(() => {
      triggerRef.current?.focus();
    }, 0);
  };

  const hasValue = value !== undefined && value !== "";

  return (
    <FieldTrigger
      hasValue={hasValue}
      disabled={disabled}
      onClear={handleClear}
      clearLabel={clearLabel}
      className={className}
    >
      <Select
        key={resetKey}
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        <SelectPrimitive.Trigger asChild>
          <FieldTriggerButton
            ref={triggerRef}
            id={id}
            data-slot="select-filter-trigger"
            aria-label={ariaLabel ?? placeholder}
            aria-describedby={ariaDescribedby}
            aria-invalid={ariaInvalid}
            className={className}
          >
            <SelectValue placeholder={placeholder} />
          </FieldTriggerButton>
        </SelectPrimitive.Trigger>
        <SelectContent>
          <SelectGroup>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.icon && <option.icon className="size-4" />}
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </FieldTrigger>
  );
};

export { SelectFilter };
export type { SelectFilterOption, SelectFilterProps };
